import type { Filter } from '@src/common/components/Filters';
import { PermitTypeList } from '@src/orders/domain/PermitType';
import type { PermitTypeFilters } from '@src/orders/services/PermitTypesClient';
import { PermitTypesClient } from '@src/orders/services/PermitTypesClient';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

const getQueryKey = (filters: PermitTypeFilters = {}) => [
  'permitTypes.list',
  filters.name,
  filters.type,
  filters.jurisdiction,
];

export const usePermitTypes = () => {
  const [appliedFilters, setAppliedFilters] = useState<PermitTypeFilters>({});
  // the list is fetched without filters initially to retrieve all possible filter values
  const { data: permitTypes = new PermitTypeList([]) } =
    useQuery<PermitTypeList>({
      queryKey: getQueryKey(),
      queryFn: () => PermitTypesClient.list(),
    });
  const { data: permitTypeList, isLoading } = useQuery<PermitTypeList>({
    queryKey: getQueryKey(appliedFilters),
    queryFn: () => PermitTypesClient.list(appliedFilters),
    placeholderData: (prev) => prev,
  });

  const filters: Filter<PermitTypeFilters>[] = [
    {
      key: 'name',
      options: [
        ...new Set(permitTypes.permitTypes.map((permit) => permit.name)),
      ],
    },
    {
      key: 'type',
      multiple: true,
      options: [
        ...new Set(permitTypes.permitTypes.map((permit) => permit.type)),
      ],
    },
    {
      key: 'jurisdiction',
      multiple: true,
      options: [
        ...new Set(
          permitTypes.permitTypes.map((permit) => permit.jurisdiction),
        ),
      ]
        .map((stateCode) => ({
          label: stateCode,
          value: stateCode,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    },
  ];

  return {
    isLoading,
    filteredPermitTypes: permitTypeList,
    permitTypes,
    filters: {
      onChange: setAppliedFilters,
      filters,
    },
  };
};
